import { ABP, ConfigStateService, RoutesService } from '@abp/ng.core';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';

/**
 * Registra rotas de menu gated por feature de forma REATIVA ao estado de configuração.
 *
 * Corrige o defeito de "módulo some do menu até dar F5" pós-login: os providers antigos capturavam
 * o valor da feature UMA vez no APP_INITIALIZER (`filter(v => v != null), take(1)`), no contexto
 * anônimo/pré-tenant onde a feature ainda vale o default 'false'. Como o login passou a navegar via
 * SPA (sem recarregar a página), o valor 'true' que chega no refreshAppState pós-login nunca
 * reativava o registro, e a rota só aparecia após um reload manual.
 *
 * Aqui esperamos a feature EFETIVAMENTE ficar 'true' (a emissão pós-login conta) e só então
 * adicionamos a rota, uma única vez. Se o tenant não tem a feature, nunca emite 'true' e a rota
 * nunca é adicionada (mesmo resultado de antes). Aceita várias features com semântica OR (ex.: o
 * módulo de Faturamento acende com FlowPay OU Confirmação de Entregas).
 */
export function addRoutesWhenAnyFeatureEnabled(
  routesService: RoutesService,
  configState: ConfigStateService,
  features: string[],
  routes: ABP.Route[],
): void {
  combineLatest(features.map(feature => configState.getFeature$(feature)))
    .pipe(
      filter(values => values.some(value => value === 'true')),
      take(1),
    )
    .subscribe(() => routesService.add([...routes]));
}

/**
 * Igual ao de cima, mas para feature de OPT-OUT (default ON no backend): o registro ACOMPANHA a
 * feature em vez de só ligar uma vez.
 *
 * Por que `addRoutesWhenAnyFeatureEnabled` não serve aqui: ele é add-only e encerra com take(1).
 * Com default ON, o boot anônimo já resolve 'true' (sem tenant vale o default do host), a rota
 * entra no menu, e o 'false' que chega no refreshAppState pós-login — tenant com o módulo
 * desligado — nunca a removeria: sobraria link morto no menu (o guard barra, mas o item fica).
 *
 * 'true' adiciona, 'false' remove; valor ausente não mexe no menu. O RoutesService casa item por
 * `name` (AbstractNavTreeService.id === 'name'), e `add` singulariza por name — religar a feature
 * não duplica o item.
 */
export function syncRoutesWithFeature(
  routesService: RoutesService,
  configState: ConfigStateService,
  feature: string,
  routes: ABP.Route[],
): void {
  configState
    .getFeature$(feature)
    .pipe(distinctUntilChanged())
    .subscribe(value => {
      if (value === 'true') {
        routesService.add([...routes]);
        return;
      }

      if (value === 'false') {
        routesService.remove(routes.map(route => route.name));
      }
    });
}
